import { SITE_URL } from "../siteConfig";
import { posts, postPath } from "../blog/posts";

export const publicPaths = ["/", "/blog", ...posts.map(postPath), "/privacy-policy", "/terms-of-service"];
export const socialImage = `${SITE_URL}/img/echo-msg-social.png`;
const organization = {
  "@type": "Organization", "@id": `${SITE_URL}/#organization`, name: "Echo MSG",
  alternateName: "ECHO MSG", url: `${SITE_URL}/`,
  logo: { "@type": "ImageObject", url: `${SITE_URL}/img/favicon_io/android-chrome-512x512.png`, width: 512, height: 512 },
  email: "echomsgteam@gmail.com",
  sameAs: ["https://www.facebook.com/profile.php?id=61595232533736", "https://www.linkedin.com/company/echo-msg/"],
};
const website = { "@type": "WebSite", "@id": `${SITE_URL}/#website`, url: `${SITE_URL}/`, name: "Echo MSG", publisher: { "@id": organization["@id"] }, inLanguage: "en" };
const knownPages = {
  "/": { title: "Echo MSG | Transcribe Voice Notes & Images in WhatsApp", description: "Transcribe voice notes and extract text from photos and screenshots with Echo MSG directly in WhatsApp. Get copyable text back in your chat. No extra app needed." },
  "/blog": { title: "WhatsApp Voice Notes & Photo-to-Text Guides | Echo MSG Blog", description: "Learn to turn WhatsApp voice notes and photos into text. Guides to transcripts, screenshot text, summaries and image quality, with steps and tool comparisons." },
  "/privacy-policy": { title: "Privacy Policy | Echo MSG", description: "How Echo MSG processes voice notes, images and text, which providers it uses, and what account and usage data it keeps." },
  "/terms-of-service": { title: "Terms of Service | Echo MSG", description: "Read Echo MSG’s terms for WhatsApp transcription and image-to-text conversion, free allowances, paid subscriptions and responsible use." },
};

export function getPageMetadata(pathname) {
  let path = pathname.replace(/\/+$/, "") || "/";
  if (path === "/terms-and-conditions") path = "/terms-of-service";
  const post = posts.find(item => postPath(item) === path);
  const page = post ? { title: `${post.title} | Echo MSG`, description: post.description } : knownPages[path];
  if (!page) return {
    title: path === "/admin" || path.startsWith("/admin/") ? "Admin | Echo MSG" : "Page not found | Echo MSG",
    description: "Visit Echo MSG for WhatsApp voice-note and photo-to-text guides.",
    robots: "noindex, nofollow", type: "website", schema: null,
  };
  const canonical = `${SITE_URL}${path === "/" ? "/" : path}`;
  const webPage = {
    "@type": path === "/blog" ? "CollectionPage" : "WebPage", "@id": `${canonical}#webpage`, url: canonical,
    name: page.title, description: page.description, isPartOf: { "@id": website["@id"] },
    about: { "@id": organization["@id"] }, inLanguage: "en",
  };
  const graph = [organization, website, webPage];
  if (path !== "/") {
    const crumbs = [{ "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` }];
    if (post) crumbs.push({ "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` });
    crumbs.push({ "@type": "ListItem", position: crumbs.length + 1, name: post ? post.title : page.title.split(" | ")[0], item: canonical });
    graph.push({ "@type": "BreadcrumbList", "@id": `${canonical}#breadcrumbs`, itemListElement: crumbs });
    webPage.breadcrumb = { "@id": `${canonical}#breadcrumbs` };
  }
  if (post) {
    webPage.mainEntity = { "@id": `${canonical}#article` };
    graph.push({
      "@type": "BlogPosting", "@id": `${canonical}#article`, headline: post.title,
      description: post.description, url: canonical, mainEntityOfPage: { "@id": webPage["@id"] },
      datePublished: post.published, dateModified: post.modified, inLanguage: "en", articleSection: post.category,
      image: { "@type": "ImageObject", url: socialImage, width: 1200, height: 630 },
      author: { "@type": "Organization", name: "Echo MSG team", url: `${SITE_URL}/blog#blog-about-title` },
      publisher: { "@id": organization["@id"] },
    });
  } else if (path === "/blog") {
    webPage.mainEntity = { "@type": "ItemList", itemListElement: posts.map((item, index) => ({ "@type": "ListItem", position: index + 1, name: item.title, url: `${SITE_URL}${postPath(item)}` })) };
  } else if (path === "/") {
    graph.push({ "@type": "Service", "@id": `${SITE_URL}/#service`, name: "Echo MSG WhatsApp voice notes and photos to text", serviceType: "Voice note transcription and image-to-text conversion", provider: { "@id": organization["@id"] }, url: canonical, description: page.description });
  }
  return { ...page, canonical, type: post ? "article" : "website", post, robots: "index, follow, max-image-preview:large", schema: { "@context": "https://schema.org", "@graph": graph } };
}

// Used by both the built HTML and client navigation so titles/canonicals cannot drift.
export function metadataElements(metadata) {
  const entries = [
    ["title", {}, metadata.title],
    ["meta", { name: "description", content: metadata.description }],
    ["meta", { name: "robots", content: metadata.robots }],
    ["meta", { property: "og:title", content: metadata.title }],
    ["meta", { property: "og:description", content: metadata.description }],
    ["meta", { property: "og:type", content: metadata.type }],
    ["meta", { property: "og:site_name", content: "Echo MSG" }],
    ["meta", { property: "og:locale", content: "en_GB" }],
    ["meta", { property: "og:image", content: socialImage }],
    ["meta", { property: "og:image:width", content: "1200" }],
    ["meta", { property: "og:image:height", content: "630" }],
    ["meta", { property: "og:image:alt", content: "Echo MSG — voice notes and photos, in text. Read it. Copy it. All inside WhatsApp." }],
    ["meta", { name: "twitter:card", content: "summary_large_image" }],
    ["meta", { name: "twitter:title", content: metadata.title }],
    ["meta", { name: "twitter:description", content: metadata.description }],
    ["meta", { name: "twitter:image", content: socialImage }],
    ["meta", { name: "twitter:image:alt", content: "Echo MSG — voice notes and photos, in text." }],
    ["link", { rel: "alternate", type: "application/rss+xml", title: "Echo MSG Blog", href: `${SITE_URL}/blog/feed.xml` }],
  ];
  if (metadata.canonical) entries.push(["link", { rel: "canonical", href: metadata.canonical }], ["meta", { property: "og:url", content: metadata.canonical }]);
  if (metadata.post) entries.push(["meta", { property: "article:published_time", content: metadata.post.published }], ["meta", { property: "article:modified_time", content: metadata.post.modified }]);
  if (metadata.schema) entries.push(["script", { type: "application/ld+json" }, JSON.stringify(metadata.schema).replace(/</g, "\\u003c")]);
  return entries;
}
